// Global
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { categoriesSelector } from '../../feature/selectors';
import { editCategory, getCategories, getCategoryDetails, toggleActiveCategory } from '../../feature';

// Components
import { Button, Skeleton } from '@/ui';
import {
  StyledContentWrapper,
  StyledTabTitleWrapper,
  StyledPageTitle,
  StyledButtonsWrap,
} from '../../components/Styled';
import { CheckmarkIcon, CloseEyeIcon, EyeIcon } from '@/icons';
import EditCategoryForm from '../../components/EditCategoryForm/EditCategoryForm';
import CategoryMarketplaceMappingsBlock from '../../components/CategoryMarketplaceMappingsBlock/CategoryMarketplaceMappingsBlock';
import { showSuccessToast, StyledActionButton, useForm } from '@/components';
import { COLORS } from '@/theme';
import { DeleteModal } from '../../components';

// Models
import { ICategoryMarketplaceMapping } from '@/models/categories.model';

// Helpers
import { EditCategoryDTO } from '../../dtos';
import { normalizeCategoryLocale } from '../../helpers';
import { useModal } from '@/hooks';
import { Resource, usePermissions } from '@/modules/Permissions';

const CategoryDetailsTabContainer = () => {
  const { t } = useTranslation(['categories']);

  const { categoryDetails, categories, isLoadingCategoryDetails } = useAppState(categoriesSelector);
  const dispatch = useAppDispatch();
  const { canManage } = usePermissions();
  const canManageCategories = canManage(Resource.CATEGORY);
  const { isOpened, openModal, closeModal } = useModal();

  const [localMappings, setLocalMappings] = useState<ICategoryMarketplaceMapping[]>([]);
  const [form] = useForm();

  const { id } = useParams();

  useEffect(() => {
    dispatch(getCategoryDetails(id as string));
    dispatch(getCategories());
  }, [id]);

  useEffect(() => {
    if (!categoryDetails || categoryDetails.id !== id) {
      return;
    }

    // Do not wipe local mappings when details payload omits marketplace_mappings
    // (e.g. after editCategory without that relation loaded).
    if (categoryDetails.marketplace_mappings !== undefined) {
      setLocalMappings(categoryDetails.marketplace_mappings);
    }
  }, [id, categoryDetails?.id, categoryDetails?.marketplace_mappings]);

  const handleEditCategory = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const editCategoryDto = new EditCategoryDTO(values);

      await dispatch(editCategory({ id: id as string, data: editCategoryDto })).unwrap();
      showSuccessToast({ message: t('edit_category_success') });
    } catch {}
  }, []);

  const handleHide = async () => {
    try {
      if (categoryDetails) {
        await dispatch(toggleActiveCategory(categoryDetails.id)).unwrap();
        showSuccessToast({ message: t('hide_category_success') });
        dispatch(getCategoryDetails(id as string));
      }
      closeModal();
    } catch {}
  };

  const getContent = () => {
    if (isLoadingCategoryDetails) return <Skeleton active />;

    if (!categoryDetails || categoryDetails.id !== id) return;

    return (
      <>
        <EditCategoryForm
          categoryDetails={normalizeCategoryLocale(categoryDetails)}
          formInstance={form}
          onSubmit={handleEditCategory}
          categories={categories}
        />
        <CategoryMarketplaceMappingsBlock
          categoryId={categoryDetails.id}
          mappings={localMappings}
          onMappingsChange={setLocalMappings}
          canAdd={(categoryDetails.sub_categories?.length ?? 0) === 0}
        />
      </>
    );
  };

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('category_details_section_title')}</StyledPageTitle>

        {canManageCategories && (
          <StyledButtonsWrap>
            <StyledActionButton
              $color={!categoryDetails?.active ? COLORS.SOFT_GRAY : COLORS.PALE_YELLOW}
              onClick={openModal}
            >
              {!categoryDetails?.active ? <CloseEyeIcon /> : <EyeIcon />}
            </StyledActionButton>
            <Button type="primary" icon={<CheckmarkIcon />} onClick={handleEditCategory}>
              {t('save_category_details')}
            </Button>
          </StyledButtonsWrap>
        )}
      </StyledTabTitleWrapper>

      <StyledContentWrapper>{getContent()}</StyledContentWrapper>

      <DeleteModal
        isOpen={isOpened}
        isLoading={isLoadingCategoryDetails}
        onClose={closeModal}
        onConfirm={handleHide}
        title={t(!categoryDetails?.active ? 'activate_category_title' : 'hide_category_title', {
          name: categoryDetails?.name,
        })}
        description={t(!categoryDetails?.active ? 'activate_category_text' : 'hide_category_text', {
          name: categoryDetails?.name,
        })}
        closeBtnText={t('delete_category_back')}
        confirmBtnText={t(!categoryDetails?.active ? 'hide_category_show' : 'hide_category_confirm')}
      />
    </>
  );
};

export default CategoryDetailsTabContainer;
